import { includes, get } from 'lodash-es'
import { camelize } from 'humps'

import { FLOWS } from 'universal/ucs'
import {
  NORMALIZATION_STATUS,
  VALIDATION_RESULT_CODE,
  ADDRESS_TYPES,
  isShippingAddressType,
  isBillingAddressType,
} from 'universal/address'
import { PRODUCT_NAMES } from 'universal/cfs-card'
import { serverData, hasFeature, hasUCPFeature } from 'utils/server-data'
import { isMobileNative } from 'utils/mobile'
import { INTENT_TYPES } from 'universal/constants'
import { isFlowForRequestPhysicalCard } from 'utils/flow'
import { getMessageFormatter } from 'utils/content'
import { CLIENT_ROUTE_PATHS } from 'utils/routes'
import { getQueryParam, getQueryParams } from 'utils/query'
import { FLOW_STEPS } from '@/constants/flow-steps'
import {
  loadAndOpenQualtricsSurveyWidget,
  PPDC_QUALTRICS_SURVEY_KEYS,
} from 'utils/widgets/qualtrics'
import { showModalPageSpinner } from '@/features/ui/ui-slice'

const { ENROLLMENT_ABANDONMENT } = PPDC_QUALTRICS_SURVEY_KEYS
const { BILLING, SHIPPING } = ADDRESS_TYPES

export const NEW_ADDRESS_ID = 'NEW_ADDRESS_ID'

const POSTAL_CODE_LENGTH = 5

export const ADDRESS_ERROR_TYPE = {
  PO_BOX_UNSUPPORTED: 'poBoxUnsupported',
  INVALID: 'invalid',
  REQUIRED: 'required',
  MISSING_REQUIRED: 'required',
  MISSING_ONE_OF: 'required',
  INVALID_PATTERN: 'invalid',
  ADDRESS_CONTAINS_PO_BOX: 'poBoxUnsupported',
}

const { ADD_ADDRESS_STEP } = FLOW_STEPS.CHANGE_ADDRESS

export const hasValidAddressDetails = (address = {}) => {
  return Object.keys(address).length > 0
}

export const isSameAddressWithoutLastFourDigitsOfPostalCode = (
  address: any = {},
  normalizedAddress: any = {},
  postalCodeLength = POSTAL_CODE_LENGTH,
) => {
  const addressPropsList = [['city'], ['line1'], ['state'], ['postalCode', postalCodeLength]]

  if (address?.line2) {
    // line2 is an optional field
    addressPropsList.push(['line2'])
  }

  return addressPropsList.every(([fieldName, trimLength]) => {
    let enteredValue = address[fieldName]
    let normalizedValue = normalizedAddress[fieldName]

    if (!enteredValue || !normalizedValue) return false

    if (trimLength) {
      enteredValue = address[fieldName].substr(0, trimLength)
      normalizedValue = normalizedAddress[fieldName].substr(0, trimLength)
    }

    if (enteredValue && normalizedValue && enteredValue === normalizedValue) {
      return true
    }

    return false
  })
}

const ADDRESS_ERROR_FIELD_MAP = {
  LINE1: 'addressLine1',
  LINE2: 'addressLine2',
  CITY: 'adminArea2',
  STATE: 'adminArea1',
  ZIP_CODE: 'postalCode',
}

export const SERVICE_ERROR = 'SERVICE_ERROR'
const { PO_BOX_UNSUPPORTED, ADDRESS_REGION_NOT_SUPPORTED, INVALID_ADDRESS } = VALIDATION_RESULT_CODE

const mapAddressErrorFields = (addressFieldArray) => {
  if (!addressFieldArray || !Array.isArray(addressFieldArray)) return []
  return addressFieldArray.reduce((errorFieldNames, fieldName) => {
    const mappedFieldName = ADDRESS_ERROR_FIELD_MAP[fieldName]
    if (mappedFieldName) {
      errorFieldNames.push(mappedFieldName)
    }
    return errorFieldNames
  }, [])
}

const getInvalidAddressFieldErrors = (validationResult) => {
  const { resultDetails } = validationResult || {}
  const { code, values } = resultDetails || {}

  if (code !== INVALID_ADDRESS) return []

  if (values && Array.isArray(values) && values.length) {
    return mapAddressErrorFields(values)
  }

  return []
}

const getInvalidNormalizedAddressFieldErrors = (validationResult) => {
  const { address = {} } = validationResult || {}
  const { normalizationStatus = '', normalizationInvalidFields = [] } = address
  const hasInvalidFields = normalizationInvalidFields?.length > 0

  if (normalizationStatus !== NORMALIZATION_STATUS.INVALID || !hasInvalidFields) {
    return []
  }

  return mapAddressErrorFields(normalizationInvalidFields)
}

export const getPOBoxUnsupportedAddressFieldErrors = (validationResult) => {
  const { resultDetails } = validationResult || {}
  const { code, values = [] } = resultDetails || {}

  if (code === PO_BOX_UNSUPPORTED) {
    return values.map((value) => ADDRESS_ERROR_FIELD_MAP[value])
  }
  return []
}

export const getAddressFormError = (validationResult) => {
  const { resultDetails } = validationResult || {}
  const { code } = resultDetails || {}

  const errorCodes = [SERVICE_ERROR, ADDRESS_REGION_NOT_SUPPORTED, INVALID_ADDRESS]

  if (getInvalidNormalizedAddressFieldErrors(validationResult).length) {
    return camelize(INVALID_ADDRESS.toLowerCase())
  }

  if (includes(errorCodes, code)) {
    return camelize(code.toLowerCase())
  }

  return ''
}

const ADDRESS_INPUT_FIELD_NAMES = {
  ADDRESS_LINE_1: 'addressLine1',
  ADDRESS_LINE_2: 'addressLine2',
  ADMIN_AREA_2: 'adminArea2',
  ADMIN_AREA_1: 'adminArea1',
  POSTAL_CODE: 'postalCode',
}

const { ADDRESS_LINE_1, ADDRESS_LINE_2, ADMIN_AREA_2, ADMIN_AREA_1, POSTAL_CODE } =
  ADDRESS_INPUT_FIELD_NAMES

const ADDRESS_FIELD_MAP = {
  [ADDRESS_LINE_1]: 'line1',
  [ADDRESS_LINE_2]: 'line2',
  [ADMIN_AREA_2]: 'city',
  [ADMIN_AREA_1]: 'state',
  [POSTAL_CODE]: 'postalCode',
}

const REVERSE_ADDRESS_FIELD_MAP = {}
for (const [key, value] of Object.entries(ADDRESS_FIELD_MAP)) {
  REVERSE_ADDRESS_FIELD_MAP[value] = key
}

/** Fallback when AddressFormat.formatToParts is unavailable (lazy WorldReady in native webviews). */
const FALLBACK_ADDRESS_FIELD_ORDER_WEB = [
  ADDRESS_LINE_1,
  ADDRESS_LINE_2,
  ADMIN_AREA_2,
  ADMIN_AREA_1,
  POSTAL_CODE,
]
const FALLBACK_ADDRESS_FIELD_ORDER_NATIVE = [
  [ADDRESS_LINE_1],
  [ADDRESS_LINE_2],
  [ADMIN_AREA_2],
  [ADMIN_AREA_1, POSTAL_CODE],
]

export const getAddressFormFieldOrder = (addressParts) => {
  // Flatten array fields for web (e.g. each field is shown on a single line).
  // Do not flatten array fields for mobile native (e.g. some fields like zip code and state can be shown on the same line).
  // Prefer live isMobileNative() over sticky serverData.isMobileNative (Venice Android PayPalQA).
  const native = isMobileNative() || !!serverData.isMobileNative
  if (!Array.isArray(addressParts) || addressParts.length === 0) {
    return native ? FALLBACK_ADDRESS_FIELD_ORDER_NATIVE : FALLBACK_ADDRESS_FIELD_ORDER_WEB
  }
  const mapFunction = native ? Array.prototype.map : Array.prototype.flatMap
  const order = mapFunction.call(addressParts, (addressPart) =>
    (addressPart?.parts || []).map((part) => REVERSE_ADDRESS_FIELD_MAP[part.type]),
  )
  if (!Array.isArray(order) || order.length === 0) {
    return native ? FALLBACK_ADDRESS_FIELD_ORDER_NATIVE : FALLBACK_ADDRESS_FIELD_ORDER_WEB
  }
  return order
}

export const getAddressFormFieldsInput = (addressObject = {}) => {
  if (!addressObject) {
    return {}
  }

  const addressFields = Object.values(ADDRESS_INPUT_FIELD_NAMES)
  const addressFieldsInput = {}
  addressFields.forEach((fieldName) => {
    addressFieldsInput[fieldName] = get(addressObject, ADDRESS_FIELD_MAP[fieldName], '')
  })
  return addressFieldsInput
}

export const ADDRESS_PAGE_NAMES = {
  CHOOSE_ADDRESS_PAGE: 'CHOOSE_ADDRESS_PAGE',
  ADD_ADDRESS_PAGE: 'ADD_ADDRESS_PAGE',
  NORMALIZED_ADDRESS_PAGE: 'NORMALIZED_ADDRESS_PAGE',
  INVALID_ADDRESS_PAGE: 'INVALID_ADDRESS_PAGE',
}

export const isFromChangeAddressFlow = (flowName) => {
  return flowName === FLOWS.CHANGE_ADDRESS
}

export const isFromAddAddressFlow = (flowName) => {
  return flowName === FLOWS.ADD_ADDRESS
}

export const isFromAddAddressPage = (pageName) => {
  return pageName === ADDRESS_PAGE_NAMES.ADD_ADDRESS_PAGE
}

const formatAddressFieldErrorMessage = ({ worldReady, fieldName, errorType }) => {
  const formatMessage = getMessageFormatter(worldReady)
  const addressFieldName = ADDRESS_FIELD_MAP[fieldName]

  if (addressFieldName) {
    return {
      [fieldName]: formatMessage(
        `changeAddress.addAddress.field.error.${addressFieldName}.${errorType}`,
      ),
    }
  }

  return {}
}

export const getAddressFieldValidationMessages = ({ worldReady, addressErrors }) => {
  let fieldErrorMessages = {}

  for (const error of addressErrors) {
    const errorType = ADDRESS_ERROR_TYPE[error.code]
    const fieldName = error.element

    if (fieldName === 'addressLine2' && addressErrors.some((e) => e.element === 'addressLine1')) {
      continue
    }

    const formattedErrorMessage = formatAddressFieldErrorMessage({
      worldReady,
      fieldName,
      errorType,
    })
    fieldErrorMessages = { ...fieldErrorMessages, ...formattedErrorMessage }
  }
  return fieldErrorMessages
}

export const getAddressFieldErrorMessages = ({
  worldReady,
  fieldErrors = [],
  errorType = ADDRESS_ERROR_TYPE.INVALID,
}) => {
  const fieldErrorMessages = fieldErrors.reduce((errorMessages, fieldName) => {
    const formattedErrorMessage = formatAddressFieldErrorMessage({
      worldReady,
      fieldName,
      errorType,
    })
    errorMessages = { ...errorMessages, ...formattedErrorMessage }
    return errorMessages
  }, {})

  return fieldErrorMessages
}

const getInvalidAddressFields = (validationResult) => {
  // invalid address errors and invalid normalized address errors are mutually exclusive
  const invalidAddressFields = getInvalidAddressFieldErrors(validationResult)
  const invalidNormalizedAddressFields = getInvalidNormalizedAddressFieldErrors(validationResult)

  if (invalidAddressFields.length > 0) return invalidAddressFields
  if (invalidNormalizedAddressFields.length > 0) return invalidNormalizedAddressFields

  return []
}

export const getAddressFieldErrors = (validationResult) => {
  // field errors are mutually exclusive
  const poBoxUnsupportedAddressFields = getPOBoxUnsupportedAddressFieldErrors(validationResult)
  if (poBoxUnsupportedAddressFields?.length > 0) {
    return {
      addressFieldErrors: poBoxUnsupportedAddressFields,
      addressErrorType: ADDRESS_ERROR_TYPE.PO_BOX_UNSUPPORTED,
    }
  }

  const invalidAddressFields = getInvalidAddressFields(validationResult)
  if (invalidAddressFields?.length > 0) {
    return {
      addressFieldErrors: invalidAddressFields,
      addressErrorType: ADDRESS_ERROR_TYPE.INVALID,
    }
  }

  return {}
}

export const hasAddressFormOrFieldErrors = (validationResult) => {
  return (
    !!getAddressFormError(validationResult) ||
    getPOBoxUnsupportedAddressFieldErrors(validationResult).length > 0
  )
}

export const isReturnUriBackToReplaceCardFlow = (returnUri) => {
  const { REPLACE_CARD } = CLIENT_ROUTE_PATHS
  const basePath = serverData.requestURI
  const replaceCardPath = `${basePath}${REPLACE_CARD.replace(':cardId', '.*')}`
  const replaceCardPathRegex = new RegExp(replaceCardPath)
  return replaceCardPathRegex.test(returnUri)
}

const FIELD_ERROR_NAME_MAP = {
  addressLine1: 'field_addressLine1',
  addressLine2: 'field_addressLine2',
  adminArea2: 'field_adminArea2',
  adminArea1: 'combo_t_field_adminArea1',
  postalCode: 'field_postalCode',
}

const fieldErrorClassName = 'ppvx_field--error___3-12-1'

const getParentElement = (fieldName): HTMLElement | null | undefined => {
  const elementName = FIELD_ERROR_NAME_MAP[fieldName]
  return document.getElementsByName(elementName)[0]?.parentNode as HTMLElement | null | undefined
}

export const setFieldErrorClassName = (fieldErrors = []) => {
  const addressFields = Object.values(ADDRESS_INPUT_FIELD_NAMES)

  addressFields.forEach((addressField) => {
    const element = getParentElement(addressField)

    if (element) {
      const elClassName = element.className || ''

      if (fieldErrors.includes(addressField)) {
        if (!elClassName.includes(fieldErrorClassName))
          element.className = `${element.className} ${fieldErrorClassName}`
      } else {
        element.className = elClassName.replace(new RegExp(`\\s?${fieldErrorClassName}`), '')
      }
    }
  })
}

export const removeFieldErrorClassName = (fieldName = '') => {
  const element = getParentElement(fieldName)

  if (element) {
    const elClassName = element.className || ''
    element.className = elClassName.replace(new RegExp(`\\s?${fieldErrorClassName}`), '')
  }
}

export const removeAllFieldErrorClassName = () => {
  const addressFields = Object.values(ADDRESS_INPUT_FIELD_NAMES)

  addressFields.forEach((addressField) => {
    removeFieldErrorClassName(addressField)
  })
}

export const hasInvalidFieldErrors = (invalidAddressFields = []) => {
  for (const invalidAddressField of invalidAddressFields) {
    const element = getParentElement(invalidAddressField)

    if (element) {
      const elClassName = element.className || ''
      if (elClassName.includes(fieldErrorClassName)) {
        return true
      }
    }
  }

  return false
}

export const trimAddressFormData = (addressFormData) => {
  Object.keys(addressFormData).forEach((field) => {
    addressFormData[field] = addressFormData[field].trim()
  })
}

export const isEligibleForEnrollmentConfirmShippingAddress = () => {
  const intent = getQueryParam('intent')
  if (!hasFeature('enableEnrollCard') || intent !== INTENT_TYPES.ENROLL) {
    return false
  }

  return true
}

// The choose-address submit button confirms a shipping address in two cases:
// enrollment (intent=ENROLL) and the BDMC shipping flow (e.g. replace card / GET_PHYSICAL_CARD).
// Both match legacy debitcardnodeweb, which labels this button "Confirm" rather than "Save".
export const shouldConfirmShippingAddress = (productName = '') => {
  if (isEligibleForEnrollmentConfirmShippingAddress()) {
    return true
  }

  const addressType = getQueryParam('addressType')
  return productName === PRODUCT_NAMES.BUSINESS_DEBIT_CARD && isShippingAddressType(addressType)
}

export const shouldSkipChooseAddressPage = () => {
  const intent = getQueryParam('intent')
  const skipAddressSelection = getQueryParam('skipAddressSelection')
  const allowedIntents = [INTENT_TYPES.ENROLL]
  return allowedIntents.includes(intent) || skipAddressSelection
}

export const loadEnrollmentAbandonmentQualtricsSurveyAndRedirect = (
  pageTag = '',
  closeUrl,
  fallback,
  options: any = {},
) => {
  const { dispatch } = options
  const cardAbandonmentQualtricsSurvey = hasUCPFeature('enableShowCardAbandonmentQualtricsSurvey')

  if (cardAbandonmentQualtricsSurvey) {
    if (dispatch) {
      dispatch(showModalPageSpinner())
    }

    loadAndOpenQualtricsSurveyWidget({
      type: ENROLLMENT_ABANDONMENT,
      pageTag,
      isOnClose: true,
      closeUrl,
      fallback,
    })
  } else if (typeof fallback === 'function') {
    fallback()
  }
}

export const isPayrollPartnerVariant = () => {
  const variant = getQueryParam('variant')
  return variant === INTENT_TYPES.PAYROLL_PARTNER
}

export const getFormattedStoredAddress = (addressString = '') => {
  if (!addressString) {
    return {}
  }

  try {
    const addressObject = JSON.parse(addressString)
    return addressObject
  } catch (e) {
    return e
  }
}

export const overrideChangeAddressFlowFirstStep = () => {
  const { nonce = '' } = getQueryParams() as any
  if (nonce) {
    return {
      firstStep: 1,
      firstStepSwitchName: ADD_ADDRESS_STEP,
    }
  }
  return {}
}

export const hasValidAddressType = (type = '') => {
  return ([BILLING, SHIPPING] as string[]).includes(type)
}
